import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { useMediaQuery } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import {
  fetchAppliances,
  clear as clearPaginatedAppliances,
  changePage
} from '#action/warehouse/appliance/appliance-paginated';
import { clear } from '#action/warehouse/warehouses';
import {
  Root,
  NoFilterData,
  AddWarehouseButton,
  AddButtonLabel,
  LoadingContainer,
  StyledPagination,
  PaginationPlaceholder
} from '#component/warehouse/site-manager/styles';
import { updateBreadcrumb } from '#action/breadcrumb';
import { fetchMeta } from '#action/meta/device';
import FilterWrapper from '#component/general/list-filter/filter-wrapper';
import { isFilterExist } from '#component/general/list-filter/helper';
import { useListFilterDataLoading } from '#component/general/list-filter/hooks';
import CardView from './appliance-cards/index';
import { getSearchParams } from './helper';
import filterConfig from './filter/config';
import ApplianceMobileView from './mobile-view';
import OverviewActions from './overview-actions';
import {
  ApplianceFilterButtonContainer,
  SiteManagerButton,
  AddApplianceButton
} from './styles';
import { useUrlPagination, useWithoutListData } from '#hooks';
import { setDocumentTitle } from '#helper';

const ApplianceCardsView = ({ history }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { warehouses } = useSelector(s => s.warehouses, shallowEqual);
  const { applianceDevices, status } = useSelector(s => s.applianceDevices, shallowEqual);
  const account = useSelector(s => s.account, shallowEqual);
  const { userType } = account;
  const {
    page,
    pages,
    appliances,
    status: applianceStatus
  } = useSelector(s => s.appliancesPaginated, shallowEqual);
  const listFilter = useSelector(s => s.listFilter, shallowEqual);
  const { pristine, warehouse } = listFilter;
  const { search: filterSearch } = warehouse || {};
  const emptySearch = !filterSearch || filterSearch.value === '';
  const filterExist = isFilterExist(warehouse) || emptySearch;
  const emptyFilter = pristine ? filterExist : true;
  const loaded = ['succeeded', 'failed'].includes(applianceStatus);
  const noFilterData = loaded && filterExist && !appliances.length;

  const filterData = useMemo(() => filterConfig, []);
  const [anchorEl, setAnchorEl] = useState(null);
  const [appliance, setAppliance] = useState({});
  const [showAllId, setShowAllId] = useState(+localStorage.getItem('showAllDevices'));
  const [withoutData, setWithoutData] = useState(false);

  const handleApplianceClick = wh => e => {
    e.stopPropagation();
    e.preventDefault();
    setAppliance(wh);
    setAnchorEl(e.currentTarget);
  };

  const fetchData = useCallback(async () => {
    await dispatch(fetchMeta());
    await dispatch(fetchAppliances());
  }, [dispatch]);

  const clearData = useCallback(async () => {
    dispatch(clear());
    dispatch(clearPaginatedAppliances());
  }, [dispatch]);

  useListFilterDataLoading(fetchData, clearData);
  useWithoutListData({ data: appliances, filterName: 'warehouse', status: applianceStatus, setWithoutData });
  useUrlPagination({ page, pages, changePage });

  useEffect(() => {
    setDocumentTitle('Site Overview');
    dispatch(updateBreadcrumb([
      { label: 'Sites', path: '/warehouse' }
    ]));
  }, [dispatch]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  const replaceHistoryParams = useCallback(
    newParams =>
      new Promise(res => {
        const { location: { search } } = window;
        const params = getSearchParams(search, newParams);

        history.replace(`?${params}`);
        res(true);
      }),
    [history]
  );

  const handlePageChange = async (event, newPage) => {
    if (page !== newPage) {
      await replaceHistoryParams({ filterType: 'page', value: newPage });
      dispatch(changePage(newPage));
    }
  };

  const SectionWrapper = loaded ? (noFilterData ? NoFilterData : Root) : Root;
  const ContentWrapper = !loaded ? LoadingContainer : (noFilterData ? NoFilterData : 'div');

  return !isMobile ? (
    <SectionWrapper as="section">
      <ApplianceFilterButtonContainer>
        <FilterWrapper data={filterData} />
        {userType < 10 ? (
          <>
            <AddWarehouseButton
              size="small"
              color="secondary"
              aria-label="Add warehouse"
              component={Link}
              to="/create-warehouse"
            >
              <AddIcon />
              <AddButtonLabel>
                Add site
              </AddButtonLabel>
            </AddWarehouseButton>
            <SiteManagerButton
              size="small"
              color="secondary"
              aria-label="Site manager"
              component={Link}
              to="/warehouse-manager"
            >
              <EditIcon />
              <AddButtonLabel>
                Site management
              </AddButtonLabel>
            </SiteManagerButton>
            <AddApplianceButton
              size="small"
              color="secondary"
              aria-label="Add appliance"
              component={Link}
              to="/warehouse/appliance/create"
            >
              <AddIcon />
              <AddButtonLabel>
                Add application
              </AddButtonLabel>
            </AddApplianceButton>
          </>
        ) : null}
      </ApplianceFilterButtonContainer>
      <ContentWrapper>
        <CardView
          appliances={appliances}
          applianceStatus={applianceStatus}
          status={status}
          account={account}
          showAllId={showAllId}
          applianceDevices={applianceDevices}
          setShowAllId={setShowAllId}
          handleApplianceClick={handleApplianceClick}
          warehouses={warehouses}
          noData={withoutData}
          loading={!loaded}
        />
      </ContentWrapper>
      {!!pages && Number(pages) > 1 ? (
        <StyledPagination
          shape="rounded"
          color="secondary"
          count={pages}
          page={page}
          onChange={handlePageChange}
        />
      ) : (
        <PaginationPlaceholder />
      )}
      <OverviewActions
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        appliance={appliance}
        userType={userType}
      />
    </SectionWrapper>
  ) : (
    <ApplianceMobileView
      appliances={appliances}
      applianceStatus={applianceStatus}
      account={account}
      showAllId={showAllId}
      applianceDevices={applianceDevices}
      setShowAllId={setShowAllId}
      handleApplianceClick={handleApplianceClick}
      warehouses={warehouses}
      isFilterSelected={!emptyFilter}
      emptyData={warehouses.filter(wh => !wh.appliances?.length)}
      page={page}
      pages={pages}
      handlePageChange={handlePageChange}
      userType={userType}
      anchorEl={anchorEl}
      setAnchorEl={setAnchorEl}
      appliance={appliance}
      loading={!loaded}
    />
  );
};

export default ApplianceCardsView;
